import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { parsePastedUrl } from './link-url';
import { verifyLink, type VerifyOutcome } from './verify';
import { CalculationDetails } from './calculation-details';
import { CopyAndQr } from './copy-and-qr';
import { TeaserNotice } from './teaser-notice';
import { BodyReachBadge } from './body-reach-badge';
import type { PaymentLinkFormatDefinition } from './format-definition';
import { payStageEntry, type PayStageEntry } from './stage-map';
// Spec 516: korrigierter Link baut bei Alias-Host-Links auf dem Alias-Origin.
import { resolveLinkBase } from './alias-hosts';

/**
 * Payment-Link Link-Tester — Modus B „Link prüfen" (Spec 501, Phase 6).
 *
 * Teaser-sicher: Paste + Auto-Parse + Warnungen laufen format-unabhaengig; das
 * Urteil (gueltig/ungueltig + Fingerprint-Diagnose) ist format-gated und bleibt
 * im Teaser deaktiviert (Format-Hinweis statt Urteil).
 */
export interface VerifyTabProps {
  format: PaymentLinkFormatDefinition;
  baseEntry: PayStageEntry;
  salt: string;
}

export function VerifyTab({ format, baseEntry, salt }: VerifyTabProps) {
  const { t } = useTranslation('payment-link');
  const [raw, setRaw] = useState('');
  // Urteil an seinen Eingabe-Schlüssel gebunden (Integrations-Review M3): bei
  // geänderter Eingabe ist das abgeleitete `outcome` SOFORT `null` (kein veraltetes
  // Urteil), bis der Effekt neu gerechnet hat.
  const [outcomeState, setOutcomeState] = useState<{
    value: VerifyOutcome;
    key: string;
  } | null>(null);

  const parsed = raw.trim() ? parsePastedUrl(raw, format) : null;

  // Unbekanntes Ziel bleibt ein format-unabhaengiger Hinweiszustand (kein Urteil).
  const ambiguous = !!parsed?.ok && parsed.unknownStage;

  // Vom Dienst ABGELEHNTE Eingaben (Handover §5): Steuerzeichen im Wert, ein
  // Segment ohne `=`, Query > 8192 Byte ODER doppelte Keys (§5 „abgelehnt", M1) —
  // kein Urteil, rote Diagnose statt Mismatch.
  const rejected =
    !!parsed?.ok &&
    (parsed.controlCharKeys.length > 0 ||
      parsed.segmentsWithoutEquals.length > 0 ||
      parsed.tooLong ||
      parsed.duplicates.length > 0);

  // Korrigierter Link baut auf dem Host der EINGEFÜGTEN URL (M2), nicht der Panel-
  // Stage — sonst zeigt „So wäre er richtig" auf die falsche Umgebung. Spec 516:
  // bei erkannten Alias-Host-Links (publicBaseUrl-Registry) bleibt entsprechend
  // der Alias-Origin die Basis, nicht die pay.<stage>-Base.
  const targetEntry =
    parsed?.ok && parsed.stage !== null
      ? parsed.aliasHost
        ? resolveLinkBase(parsed.baseUrl, payStageEntry(parsed.stage))
        : payStageEntry(parsed.stage)
      : baseEntry;

  const outcomeKey = `${raw}|${salt}`;
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (
        !format.available ||
        !parsed ||
        !parsed.ok ||
        !salt ||
        ambiguous ||
        rejected
      ) {
        setOutcomeState(null);
        return;
      }
      const result = await verifyLink(format, salt, targetEntry, {
        params: parsed.params,
        signature: parsed.signature,
      });
      if (!cancelled) {
        setOutcomeState({ value: result, key: outcomeKey });
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
    // parsed ist aus raw+format abgeleitet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw, salt, format, baseEntry, ambiguous, rejected]);

  // Nur gültig, wenn das Urteil zu den AKTUELLEN Eingaben passt (M3).
  const outcome =
    outcomeState && outcomeState.key === outcomeKey ? outcomeState.value : null;

  const foreignStage =
    parsed?.ok &&
    parsed.stage !== null &&
    parsed.stage !== baseEntry.stage;

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="tester-paste">{t('tester.verify.pasteLabel')}</Label>
        <Textarea
          id="tester-paste"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder={t('tester.verify.pastePlaceholder')}
          spellCheck={false}
          rows={3}
          className="font-mono text-xs"
        />
      </div>

      {parsed && !parsed.ok && (
        <p className="text-sm text-destructive">
          {t('tester.verify.notParseable')}
        </p>
      )}

      {parsed?.ok && (
        <div className="space-y-2" data-testid="parsed-params">
          <Label>{t('tester.verify.parsedTitle')}</Label>
          <div className="space-y-1">
            {parsed.params.map((p, i) => (
              <div
                key={`${i}-${p.key}`}
                className="flex items-center gap-2 font-mono text-xs"
              >
                <span className="text-muted-foreground">
                  {p.key}={p.value}
                </span>
                <BodyReachBadge format={format} paramKey={p.key} />
              </div>
            ))}
          </div>

          {parsed.duplicates.length > 0 && (
            <p className="text-xs text-destructive" data-testid="reject-duplicate">
              {t('tester.verify.warnings.duplicate', {
                keys: parsed.duplicates.join(', '),
              })}
            </p>
          )}
          {parsed.missingSignature && (
            <p className="text-xs text-amber-700">
              {t('tester.verify.warnings.missingSignature')}
            </p>
          )}
          {parsed.unknownStage && (
            <p className="text-xs text-amber-700">
              {t('tester.verify.warnings.unknownStage')}
            </p>
          )}
          {/* Spec 516: erkannter Merchant-Alias-Host — positive Einordnung
              statt Unbekannt-Diagnose. */}
          {parsed.aliasHost && (
            <p className="text-xs text-muted-foreground" data-testid="alias-host-hint">
              {t('tester.verify.aliasHostHint', { stage: parsed.stage })}
            </p>
          )}
          {foreignStage && (
            <p className="flex items-center gap-1 text-xs text-amber-700">
              <AlertTriangle className="h-3 w-3" aria-hidden="true" />
              {t('tester.verify.warnings.foreignStage', {
                stage: parsed.stage,
              })}
            </p>
          )}
          {/* Vom Dienst abgelehnte Eingaben (Handover §5). */}
          {parsed.controlCharKeys.length > 0 && (
            <p className="text-xs text-destructive" data-testid="reject-control">
              {t('tester.verify.reject.controlChars', {
                keys: parsed.controlCharKeys.join(', '),
              })}
            </p>
          )}
          {parsed.segmentsWithoutEquals.length > 0 && (
            <p className="text-xs text-destructive" data-testid="reject-noequals">
              {t('tester.verify.reject.segmentsWithoutEquals', {
                segments: parsed.segmentsWithoutEquals.join(', '),
              })}
            </p>
          )}
          {parsed.tooLong && (
            <p className="text-xs text-destructive" data-testid="reject-toolong">
              {t('tester.verify.reject.tooLong')}
            </p>
          )}
        </div>
      )}

      {/* Urteil: format-gated UND nur bei eindeutiger Eingabe (kein Urteil bei
          doppelten Parametern / unbekanntem Ziel — Spec §Edge-Cases Modus B). */}
      {!format.available ? (
        <TeaserNotice text={t('tester.verify.placeholder')} />
      ) : ambiguous ? (
        <p className="text-sm text-muted-foreground" data-testid="verdict-suppressed">
          {t('tester.verify.warnings.noVerdict')}
        </p>
      ) : (
        outcome && <VerdictCard outcome={outcome} baseEntry={targetEntry} />
      )}
    </div>
  );
}

function VerdictCard({
  outcome,
  baseEntry,
}: {
  outcome: VerifyOutcome;
  baseEntry: PayStageEntry;
}) {
  const { t } = useTranslation('payment-link');

  if (outcome.state === 'inputError') {
    return (
      <p className="text-sm text-muted-foreground" data-testid="verdict-input-error">
        {t(`tester.verify.inputError.${outcome.inputError}`)}
      </p>
    );
  }

  if (outcome.state === 'valid') {
    return (
      <div
        className="flex items-start gap-2 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900"
        data-testid="verdict-valid"
      >
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <div className="space-y-2">
          <p>{t('tester.verify.result.valid')}</p>
          <CalculationDetails
            stringToSign={outcome.stringToSign ?? ''}
            expected={outcome.expected ?? ''}
            received={outcome.received}
          />
        </div>
      </div>
    );
  }

  // invalid
  const hit = outcome.fingerprints?.[0];
  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-md border p-3 text-sm',
        'border-rose-300 bg-rose-50 text-rose-900',
      )}
      data-testid="verdict-invalid"
    >
      <XCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <div className="space-y-2">
        {outcome.uppercaseSignatureRejected ? (
          <>
            <p className="font-medium">
              {t('tester.verify.result.uppercaseTitle')}
            </p>
            <p data-testid="diagnosis-uppercase">
              {t('tester.verify.result.uppercaseHint')}
            </p>
          </>
        ) : hit ? (
          <>
            <p className="font-medium">
              {t('tester.verify.result.invalidWithHit')}
            </p>
            <p data-testid="diagnosis">
              {hit.id.startsWith('missingRequired:')
                ? t(hit.diagnoseKey, {
                    name: hit.id.slice('missingRequired:'.length),
                  })
                : t(hit.diagnoseKey)}
            </p>
          </>
        ) : (
          <>
            <p className="font-medium">
              {t('tester.verify.result.invalidNoHit')}
            </p>
            <p>{t('tester.verify.saltChecklist')}</p>
          </>
        )}
        <CalculationDetails
          stringToSign={outcome.stringToSign ?? ''}
          expected={outcome.expected ?? ''}
          received={outcome.received}
        />
        {outcome.correctedUrl && (
          <div className="space-y-1">
            <p className="text-xs font-medium">
              {t('tester.verify.correctedTitle')}
            </p>
            <CopyAndQr
              url={outcome.correctedUrl}
              enabled={!baseEntry.placeholder}
            />
          </div>
        )}
      </div>
    </div>
  );
}
